<template>
  <BaseContainer>
    <div v-if="program" class="flex flex-col gap-5 mb-10">
      <ProgramDescription
        :program="program"
      />
      <!-- A configuration that stops working — a card replaced, a code
           changed — has to be replayable without deactivating the module. -->
      <div v-if="configurableExtension" class="mx-6 flex justify-end">
        <BaseButton @click="ExtensionNavigationService.showExtensionConfiguration(configurableExtension.id, router)">
          <Settings class="mr-1.5 h-4 w-4" />
          Configurer
        </BaseButton>
      </div>
      <PollToggle
        v-if="togglablePolls.length > 0"
        :polls="togglablePolls"
        class="mx-6"
      />
      <FeatureStatistics
        v-if="program.programmable_type === 'feature'"
        :program="program"
      />
      <BrandOfferDiscount
        v-if="program.programmable_type==='discount'"
        :discount-id="program.programmable_id"
      />
      <SupportProgramDetailStatistics
        v-if="program.programmable_type === 'support'"
        :program="program"
      />
      <LoyaltyCardStatistics
        v-if="program.programmable_type === 'loyalty-card'"
        class="mx-5"
        :programmable-id="program.programmable_id"
      />
    </div>
  </BaseContainer>
</template>

<script lang="ts" setup>
import BrandOfferDiscount from '@/brand/components/BrandOfferDiscount.vue';
import SupportProgramDetailStatistics
    from '@/program/components/support-program/SupportProgramDetailStatistics.vue';
import BaseContainer from '@/core/components/base/BaseContainer.vue';
import ProgramDescription from '@/program/components/program-description/ProgramDescription.vue';
import LoyaltyCardStatistics from '@/program/components/loyalty-card-statistics/LoyaltyCardStatistics.vue';
import FeatureStatistics from '@/program/components/feature-statistics/FeatureStatistics.vue';
import {computed, onMounted} from "vue";
import {useRouter} from "vue-router";
import {Settings} from "@lucide/vue";
import {Program} from "@/program/interfaces/Program";
import PollToggle from "@/program/components/polls/PollToggle.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import ExtensionNavigationService from "@/extension/services/ExtensionNavigationService";
import ExtensionService from "@/extension/services/ExtensionService";
import {useExtensionStore} from "@/stores/extension.store";

const props = defineProps<{
    program: Program
}>();

const router = useRouter();
const extensionStore = useExtensionStore();

/** The polls exposed to the activation widget: display_in_program polls are always active. */
const togglablePolls = computed(() => props.program.polls?.filter(poll => !poll.display_in_program) ?? []);

/**
 * The module behind this program, when it has a configuration of its own.
 *
 * The slug is carried by the extension rather than by the program: a pharmacy
 * activates a module, and what there is to set up belongs to that module. The
 * module names its own programmable (`sub_id`, among the bundle's types in
 * `sub_type`): a sibling program of the same bundle is not it.
 */
const configurableExtension = computed(() => {
    const {id, programmable_id: programmableId, programmable_type: programmableType} = props.program;

    const extension = extensionStore.extensions?.find(candidate =>
        ExtensionService.isProgramExtension(candidate)
        && candidate.sub_id === programmableId
        && String(candidate.sub_type).split(',').includes(programmableType)
        && candidate.program_bundle?.programs?.some(program => program.id === id)
    );

    return extension?.configurator ? extension : null;
});

onMounted(() => {
    // Reaching this page from a link rather than from the module store leaves
    // the extensions unloaded, and the module unfindable.
    if (!extensionStore.extensions) {
        extensionStore.loadExtensions();
    }
});

</script>
